import axios from 'axios';
import {
  Appeal,
  Call,
  Campaign,
  Contact,
  IncomingCall,
  LLMTemplate,
  LoginCredentials,
  LoginResponse,
  QueueJobs,
  QueueStatus,
  User,
  ContactFormData,
} from './interfaces';

const API_URL = 'https://api.solutions-and-researches.pro/';
// const API_URL = 'http://localhost:8080/';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// API функции
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  },
  logout: async (): Promise<{ success: boolean }> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get<User>('/auth/profile');
    return response.data;
  },
};

export const callsAPI = {
  getCalls: async (params?: { page?: number; campaignName?: string }) => {
    const response = await api.get('call', { params });
    return response.data as [Call[], number];
  },
  getRecordingUrl: (callId: string) =>
    `${api.defaults.baseURL}call/${callId}/recording/outgoing`,
  getIncomingRecordingUrl: (callId: string) =>
    `${api.defaults.baseURL}call/${callId}/recording/incoming`,
  getIncomingCalls: async (params?: { page?: number }) => {
    const response = await api.get('call/incoming', { params });
    return response.data as [IncomingCall[], number];
  },
};

export const campaignAPI = {
  createCampaign: async (data: {
    businessDays: any;
    description?: string;
    endTime: any;
    file: File;
    maxCallDurationSec: any;
    name: string;
    startTime: any;
    templateId: any;
    timezone: any;
    ttsVoiceModel: any;
  }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('templateId', data.templateId);
    formData.append('businessDays', JSON.stringify(data.businessDays));
    formData.append('startTime', data.startTime);
    formData.append('endTime', data.endTime);
    formData.append('timezone', data.timezone);
    formData.append('ttsProvider', 'deepgram');
    formData.append('maxCallDurationSec', String(data.maxCallDurationSec));
    formData.append('ttsVoiceModel', data.ttsVoiceModel);
    const response = await api.post('campaigns', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  deleteCampaign: async (id: string) => {
    const response = await api.delete(`campaigns/${id}`);
    return response.data;
  },
  getAllCampaigns: async (params?: {
    page?: any;
    name?: any;
    createdAtFrom?: any;
    createdAtTo?: any;
    status?: any;
  }) => {
    const response = await api.get('campaigns', { params });
    return response.data as [Campaign[], number];
  },
  getCampaignsForFilter: async (): Promise<Campaign[]> => {
    const response = await api.get('campaigns/unique');
    return response.data;
  },
  startCampaign: async (id: string) => {
    const response = await api.post(`campaigns/${id}/start`);
    return response.data;
  },
  updateCampaign: async (
    id: string,
    data: Partial<Campaign>
  ): Promise<Campaign> => {
    const response = await api.put(`campaigns/${id}`, data);
    return response.data;
  },
  pauseCampaign: async (id: string) => {
    const response = await api.post(`campaigns/${id}/pause`);
    return response.data;
  },
  resumeCampaign: async (id: string) => {
    const response = await api.post(`campaigns/${id}/resume`);
    return response.data;
  },
};

export const contactsAPI = {
  getContacts: async (params?: { page?: number; campaignName?: string }) => {
    const response = await api.get('contacts', { params });
    return response.data as [Contact[], number];
  },
  updateContact: async (
    id: string,
    data: Partial<Contact>
  ): Promise<Contact> => {
    const response = await api.put(`contacts/${id}`, data);
    return response.data;
  },
  deleteContact: async (id: string): Promise<void> => {
    await api.delete(`contacts/${id}`);
  },
};

export const healthAPI = {
  getServicesHealth: async () => {
    const response = await api.get('health/services');
    return response.data;
  },
};

export const llmTemplatesAPI = {
  createTemplate: async (data: Partial<LLMTemplate>): Promise<LLMTemplate> => {
    const response = await api.post('llm-templates', data);
    return response.data;
  },
  deleteTemplate: async (id: string): Promise<LLMTemplate> => {
    const response = await api.delete(`llm-templates/${id}`);
    return response.data;
  },
  getInitialPrompt: async () => {
    const response = await api.get('llm-templates/initial');
    return response.data;
  },
  getTemplates: async (): Promise<LLMTemplate[]> => {
    const response = await api.get('llm-templates');
    return response.data;
  },
  updateTemplate: async (
    id: string,
    data: Partial<LLMTemplate>
  ): Promise<LLMTemplate> => {
    const response = await api.put(`llm-templates/${id}`, data);
    return response.data;
  },
};

export const todoApi = {
  getTodos: async () => {
    const response = await api.get('todos');
    return response.data;
  },
  createTodo: async (data: Record<string, unknown>) => {
    const response = await api.post('todos', data);
    return response.data;
  },
  updateTodo: async (data: Record<string, unknown>) => {
    const response = await api.put('todos', data);
    return response.data;
  },
  deleteTodo: async (id: string) => {
    const response = await api.delete(`${id}/todos`);
    return response.data;
  },
};

export const queueApi = {
  getJobs: async (): Promise<QueueJobs> => {
    const response = await api.get('queue/campaign-calls/jobs');
    return response.data;
  },
  getStatus: async (): Promise<QueueStatus> => {
    const response = await api.get('queue/campaign-calls/status');
    return response.data;
  },
};

export const appealsAPI = {
  getAppeals: async (params?: { page?: number }) => {
    const response = await api.get('appeals', { params });
    return response.data as [Appeal[], number];
  },
};

export const contactAPI = {
  sendMessage: async (data: ContactFormData): Promise<{ success: boolean }> => {
    const response = await api.post('appeals', data);
    return response.data;
  },
};
